// src/features/admin/components/settings/system-preferences/components/SecuritySettings.tsx
import React, { useState } from 'react';
import { ShieldCheckIcon, ClockIcon, LockClosedIcon, DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SESSION_TIMEOUTS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes (Recommended)' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
];

const SecuritySettings: React.FC = () => {
  const [sessionTimeout, setSessionTimeout] = useState(30);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [auditLogging, setAuditLogging] = useState(true);
  const [confidentialityMode, setConfidentialityMode] = useState(false);
  const [documentWatermarking, setDocumentWatermarking] = useState(false);

  return (
    <div className="security-settings space-y-8">
      {/* Session Timeout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <ClockIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Session Timeout
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Automatically log out users after a period of inactivity to protect sensitive data.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={sessionTimeout}
            onChange={(e) => setSessionTimeout(Number(e.target.value))}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {SESSION_TIMEOUTS.map(timeout => (
              <option key={timeout.value} value={timeout.value}>{timeout.label}</option>
            ))}
          </select>

          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            💡 Tip: Shorter timeouts increase security but may interrupt workflows. 30 minutes is recommended for most law firms.
          </p>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <LockClosedIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Two-Factor Authentication (2FA)
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Require all users to enable two-factor authentication for enhanced security.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={twoFactorRequired}
              onChange={(e) => setTwoFactorRequired(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Require 2FA for All Users
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Users must set up 2FA (authenticator app or SMS) before accessing the system
              </div>
            </div>
          </label>

          {twoFactorRequired && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                ⚠️ Enabling this requirement will force all users to set up 2FA on their next login.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Audit Logging */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentMagnifyingGlassIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Audit Logging
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Track detailed activity logs for compliance and security monitoring.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={auditLogging}
              onChange={(e) => setAuditLogging(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Enable Detailed Activity Logging
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Records user actions, document access, and system changes for compliance and forensics
              </div>
            </div>
          </label>

          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Logged events include: Document creation/editing, user logins, matter changes, privilege log access, and administrative actions.
          </p>
        </div>
      </div>

      {/* Client Confidentiality Mode */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <ShieldCheckIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Client Confidentiality Mode
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Additional protection for sensitive client information.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={confidentialityMode}
              onChange={(e) => setConfidentialityMode(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Enable Confidentiality Mode
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Masks sensitive data in screenshots, blocks copy/paste for confidential documents, and adds extra access controls
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Document Watermarking */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentMagnifyingGlassIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Document Watermarking
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Automatically add watermarks to drafts and confidential documents.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={documentWatermarking}
              onChange={(e) => setDocumentWatermarking(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Auto-Watermark Drafts
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Adds "DRAFT" or "CONFIDENTIAL" watermark to documents marked as non-final
              </div>
            </div>
          </label>

          {documentWatermarking && (
            <div className="mt-4 p-3 bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded">
              <p className="text-sm text-lex-accent dark:text-lex-accent">
                📝 Watermarks are applied during PDF export and do not appear in the editor.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-md p-4">
        <div className="flex">
          <ShieldCheckIcon className="h-5 w-5 text-lex-accent dark:text-lex-accent mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-lex-accent dark:text-lex-accent">
            <p className="font-medium mb-1">Security Best Practices</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Require 2FA for all users handling sensitive client data</li>
              <li>Keep session timeouts under 1 hour for shared workstations</li>
              <li>Enable audit logging for compliance with bar association rules</li>
              <li>Review security logs regularly for suspicious activity</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;

